import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export interface ResourceGroupRow {
  id: string;
  clinic_id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  sort_order: number;
}

export interface ResourceRow {
  id: string;
  clinic_id: string;
  resource_group_id: string;
  name: string;
  is_active: boolean;
  sort_order: number;
  availability: Record<string, { enabled: boolean; start: string; end: string }> | null;
}

export interface ServiceResourceRequirementRow {
  id: string;
  clinic_id: string;
  service_id: string;
  resource_group_id: string;
  quantity: number;
  eligibility_mode: 'any' | 'selected';
  eligible_resource_ids: string[];
}

export interface AppointmentResourceAllocationRow {
  id: string;
  appointment_id: string;
  clinic_id: string;
  resource_id: string | null;
  resource_group_id: string | null;
  resource_name: string;
  resource_group_name: string;
  occupies: boolean;
}

export interface ServiceResourceRequirementInput {
  resource_group_id: string;
  quantity: number;
  eligibility_mode: 'any' | 'selected';
  eligible_resource_ids: string[];
}

export interface ServicePractitionerRow {
  clinic_id: string;
  service_id: string;
  practitioner_id: string;
}

export type PractitionerEligibilityMode = 'all' | 'selected';

export interface SlotResourceOption {
  resource_id: string;
  resource_group_id: string;
  resource_name: string;
  resource_group_name: string;
  is_available: boolean;
}

export const resourceGroupsQueryKey = (clinicId: string | null) =>
  ['resource-groups', clinicId] as const;

export const resourcesQueryKey = (clinicId: string | null) => ['resources', clinicId] as const;

function serviceScopeKey(serviceId?: string | null): 'all' | 'none' | string {
  if (serviceId === undefined) return 'all';
  if (!serviceId) return 'none';
  return serviceId;
}

export const serviceResourceRequirementsQueryKey = (
  clinicId: string | null,
  serviceId?: string | null,
) => ['service-resource-requirements', clinicId, serviceScopeKey(serviceId)] as const;

export const servicePractitionersQueryKey = (clinicId: string | null, serviceId?: string | null) =>
  ['service-practitioners', clinicId, serviceScopeKey(serviceId)] as const;

export const appointmentResourceAllocationsQueryKey = (appointmentId: string | null | undefined) =>
  ['appointment-resource-allocations', appointmentId ?? null] as const;

export const occupyingAllocationsQueryKey = (clinicId: string | null, appointmentIds: string[]) =>
  ['appointment-resource-allocations-range', clinicId, appointmentIds] as const;

export const occupyingAllocationsRangeQueryKey = (
  clinicId: string | null,
  start: string,
  end: string,
) => ['occupying-resource-allocations', clinicId, start, end] as const;

export function useResourceGroups(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: resourceGroupsQueryKey(clinicId ?? null),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<ResourceGroupRow[]> => {
      if (!clinicId) return [];
      const { data, error } = await supabase
        .from('resource_groups' as never)
        .select('id, clinic_id, name, description, is_active, sort_order')
        .eq('clinic_id', clinicId)
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true });
      if (error) throw error;
      return (data ?? []) as ResourceGroupRow[];
    },
    staleTime: 30_000,
  });
}

export function useResources(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: resourcesQueryKey(clinicId ?? null),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<ResourceRow[]> => {
      if (!clinicId) return [];
      const { data, error } = await supabase
        .from('resources' as never)
        .select('id, clinic_id, resource_group_id, name, is_active, sort_order, availability')
        .eq('clinic_id', clinicId)
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true });
      if (error) throw error;
      return (data ?? []) as ResourceRow[];
    },
    staleTime: 30_000,
  });
}

export function useServiceResourceRequirements(
  clinicId: string | null | undefined,
  serviceId?: string | null,
) {
  const loadAllForClinic = serviceId === undefined;
  return useQuery({
    queryKey: serviceResourceRequirementsQueryKey(clinicId ?? null, serviceId),
    enabled: Boolean(clinicId) && (loadAllForClinic || Boolean(serviceId)),
    queryFn: async (): Promise<ServiceResourceRequirementRow[]> => {
      if (!clinicId) return [];
      if (!loadAllForClinic && !serviceId) return [];
      let query = supabase
        .from('service_resource_requirements' as never)
        .select('id, clinic_id, service_id, resource_group_id, quantity, eligibility_mode')
        .eq('clinic_id', clinicId);
      if (serviceId) {
        query = query.eq('service_id', serviceId);
      }
      const { data, error } = await query;
      if (error) throw error;
      const rows = (data ?? []) as Array<
        Omit<ServiceResourceRequirementRow, 'eligible_resource_ids'>
      >;
      if (rows.length === 0) return [];

      const { data: eligibleRows, error: eligibleError } = await supabase
        .from('service_resource_requirement_resources' as never)
        .select('requirement_id, resource_id')
        .eq('clinic_id', clinicId)
        .in(
          'requirement_id',
          rows.map((row) => row.id),
        );
      if (eligibleError) throw eligibleError;

      const byRequirement = new Map<string, string[]>();
      for (const row of (eligibleRows ?? []) as Array<{
        requirement_id: string;
        resource_id: string;
      }>) {
        const list = byRequirement.get(row.requirement_id) ?? [];
        list.push(row.resource_id);
        byRequirement.set(row.requirement_id, list);
      }

      return rows.map((row) => ({
        ...row,
        eligibility_mode: row.eligibility_mode === 'selected' ? 'selected' : 'any',
        eligible_resource_ids: byRequirement.get(row.id) ?? [],
      }));
    },
    staleTime: 30_000,
  });
}

export function useAppointmentResourceAllocations(appointmentId: string | null | undefined) {
  return useQuery({
    queryKey: appointmentResourceAllocationsQueryKey(appointmentId),
    enabled: Boolean(appointmentId),
    queryFn: async (): Promise<AppointmentResourceAllocationRow[]> => {
      if (!appointmentId) return [];
      const { data, error } = await supabase
        .from('appointment_resource_allocations' as never)
        .select(
          'id, appointment_id, clinic_id, resource_id, resource_group_id, resource_name, resource_group_name, occupies',
        )
        .eq('appointment_id', appointmentId)
        .eq('occupies', true);
      if (error) throw error;
      return (data ?? []) as AppointmentResourceAllocationRow[];
    },
    staleTime: 15_000,
  });
}

export function useOccupyingAllocationsForAppointments(
  clinicId: string | null | undefined,
  appointmentIds: string[],
  enabled = true,
) {
  const ids = [...new Set(appointmentIds)].sort();
  return useQuery({
    queryKey: occupyingAllocationsQueryKey(clinicId ?? null, ids),
    enabled: Boolean(clinicId && enabled),
    queryFn: async (): Promise<AppointmentResourceAllocationRow[]> => {
      if (!clinicId || ids.length === 0) return [];
      const { data, error } = await supabase
        .from('appointment_resource_allocations' as never)
        .select(
          'id, appointment_id, clinic_id, resource_id, resource_group_id, resource_name, resource_group_name, occupies',
        )
        .eq('clinic_id', clinicId)
        .eq('occupies', true)
        .in('appointment_id', ids);
      if (error) throw error;
      return (data ?? []) as AppointmentResourceAllocationRow[];
    },
    staleTime: 15_000,
  });
}

export function useCreateResourceGroup(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; description?: string | null }) => {
      if (!clinicId) throw new Error('No active clinic');
      const { data, error } = await supabase
        .from('resource_groups' as never)
        .insert({
          clinic_id: clinicId,
          name: input.name.trim(),
          description: input.description ?? null,
        } as never)
        .select('id, clinic_id, name, description, is_active, sort_order')
        .single();
      if (error) throw error;
      return data as ResourceGroupRow;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: resourceGroupsQueryKey(clinicId ?? null) });
    },
  });
}

export function useUpdateResourceGroup(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      name?: string;
      description?: string | null;
      is_active?: boolean;
    }) => {
      const { data, error } = await supabase
        .from('resource_groups' as never)
        .update({
          ...(input.name !== undefined ? { name: input.name.trim() } : {}),
          ...(input.description !== undefined ? { description: input.description } : {}),
          ...(input.is_active !== undefined ? { is_active: input.is_active } : {}),
        } as never)
        .eq('id', input.id)
        .select('id, clinic_id, name, description, is_active, sort_order')
        .single();
      if (error) throw error;
      return data as ResourceGroupRow;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: resourceGroupsQueryKey(clinicId ?? null) });
    },
  });
}

export function useCreateResource(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      resource_group_id: string;
      name: string;
      is_active?: boolean;
      availability?: ResourceRow['availability'];
    }) => {
      if (!clinicId) throw new Error('No active clinic');
      const { data, error } = await supabase
        .from('resources' as never)
        .insert({
          clinic_id: clinicId,
          resource_group_id: input.resource_group_id,
          name: input.name.trim(),
          is_active: input.is_active ?? true,
          availability: input.availability ?? null,
        } as never)
        .select('id, clinic_id, resource_group_id, name, is_active, sort_order, availability')
        .single();
      if (error) throw error;
      return data as ResourceRow;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: resourcesQueryKey(clinicId ?? null) });
    },
  });
}

export function useUpdateResource(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      name?: string;
      is_active?: boolean;
      availability?: ResourceRow['availability'] | null;
    }) => {
      const { data, error } = await supabase
        .from('resources' as never)
        .update({
          ...(input.name !== undefined ? { name: input.name.trim() } : {}),
          ...(input.is_active !== undefined ? { is_active: input.is_active } : {}),
          ...(input.availability !== undefined ? { availability: input.availability } : {}),
        } as never)
        .eq('id', input.id)
        .select('id, clinic_id, resource_group_id, name, is_active, sort_order, availability')
        .single();
      if (error) throw error;
      return data as ResourceRow;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: resourcesQueryKey(clinicId ?? null) });
    },
  });
}

export function useServicePractitioners(
  clinicId: string | null | undefined,
  serviceId?: string | null,
) {
  const loadAllForClinic = serviceId === undefined;
  return useQuery({
    queryKey: servicePractitionersQueryKey(clinicId ?? null, serviceId),
    enabled: Boolean(clinicId) && (loadAllForClinic || Boolean(serviceId)),
    queryFn: async (): Promise<ServicePractitionerRow[]> => {
      if (!clinicId) return [];
      if (!loadAllForClinic && !serviceId) return [];
      let query = supabase
        .from('service_practitioners' as never)
        .select('clinic_id, service_id, practitioner_id')
        .eq('clinic_id', clinicId);
      if (serviceId) {
        query = query.eq('service_id', serviceId);
      }
      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as ServicePractitionerRow[];
    },
    staleTime: 30_000,
  });
}

export function useReplaceServiceResourceRequirements(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      service_id: string;
      practitioner_eligibility: PractitionerEligibilityMode;
      practitioner_ids: string[];
      requirements: ServiceResourceRequirementInput[];
    }) => {
      if (!clinicId) throw new Error('No active clinic');
      const { error } = await supabase.rpc(
        'replace_service_booking_config' as never,
        {
          p_clinic_id: clinicId,
          p_service_id: input.service_id,
          p_practitioner_eligibility: input.practitioner_eligibility,
          p_practitioner_ids: input.practitioner_ids,
          p_requirements: input.requirements.map((req) => ({
            resource_group_id: req.resource_group_id,
            quantity: Math.max(1, Math.min(100, Math.trunc(req.quantity))),
            eligibility_mode: req.eligibility_mode,
            resource_ids: req.eligible_resource_ids,
          })),
        } as never,
      );
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['service-resource-requirements', clinicId ?? null] });
      qc.invalidateQueries({ queryKey: ['service-practitioners', clinicId ?? null] });
      qc.invalidateQueries({ queryKey: ['services', 'list', clinicId ?? null] });
      qc.invalidateQueries({ queryKey: ['public-booking'] });
    },
  });
}

export function useFilterSlotsByResourceCapacity(
  clinicId: string | null | undefined,
  serviceId: string | null | undefined,
  starts: string[],
  ends: string[],
  excludeAppointmentId?: string | null,
) {
  return useQuery({
    queryKey: [
      'resource-slot-flags',
      clinicId ?? null,
      serviceId ?? null,
      starts,
      ends,
      excludeAppointmentId ?? null,
    ],
    enabled: Boolean(clinicId && serviceId && starts.length > 0 && starts.length === ends.length),
    queryFn: async (): Promise<boolean[]> => {
      const { data, error } = await supabase.rpc(
        'filter_intervals_satisfying_service_resources' as never,
        {
          p_clinic_id: clinicId,
          p_service_id: serviceId,
          p_starts: starts,
          p_ends: ends,
          p_exclude_appointment_id: excludeAppointmentId ?? undefined,
        } as never,
      );
      if (error) throw error;
      return (data ?? []) as boolean[];
    },
    staleTime: 15_000,
  });
}

export function useAvailableResourcesForSlot(
  clinicId: string | null | undefined,
  serviceId: string | null | undefined,
  startsAt: string | null | undefined,
  endsAt: string | null | undefined,
  excludeAppointmentId?: string | null,
) {
  return useQuery({
    queryKey: [
      'available-resources-for-slot',
      clinicId ?? null,
      serviceId ?? null,
      startsAt ?? null,
      endsAt ?? null,
      excludeAppointmentId ?? null,
    ],
    enabled: Boolean(clinicId && serviceId && startsAt && endsAt),
    queryFn: async (): Promise<SlotResourceOption[]> => {
      const { data, error } = await supabase.rpc(
        'list_available_resources_for_slot' as never,
        {
          p_clinic_id: clinicId,
          p_service_id: serviceId,
          p_starts_at: startsAt,
          p_ends_at: endsAt,
          p_exclude_appointment_id: excludeAppointmentId ?? undefined,
        } as never,
      );
      if (error) throw error;
      return (data ?? []) as SlotResourceOption[];
    },
    staleTime: 15_000,
  });
}

export function useOccupyingAllocationsInRange(
  clinicId: string | null | undefined,
  start: string,
  end: string,
) {
  return useQuery({
    queryKey: occupyingAllocationsRangeQueryKey(clinicId ?? null, start, end),
    enabled: Boolean(clinicId && start && end),
    queryFn: async (): Promise<AppointmentResourceAllocationRow[]> => {
      if (!clinicId) return [];
      const { data, error } = await supabase
        .from('appointment_resource_allocations' as never)
        .select(
          'id, appointment_id, clinic_id, resource_id, resource_group_id, resource_name, resource_group_name, occupies',
        )
        .eq('clinic_id', clinicId)
        .eq('occupies', true)
        .gte('starts_at', start)
        .lte('starts_at', end);
      if (error) throw error;
      return (data ?? []) as AppointmentResourceAllocationRow[];
    },
    staleTime: 15_000,
  });
}

export function occupyingResourceLabel(
  rows: AppointmentResourceAllocationRow[] | undefined,
): string | null {
  if (!rows || rows.length === 0) return null;
  return rows.map((row) => row.resource_name).join(', ');
}
